import { Box, Paper, Typography, Alert, AlertTitle, Chip, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { Lock, Phone, QrCode } from "@mui/icons-material";
import { TransactionForm } from "./TransactionForm";

interface Transaction {
    id: string | number;
    type: "EARN" | "REDEEM_DIRECT" | "REDEEM_RETURN";
    amountEGP?: number | null;
    pointsDelta?: number | null;
    cashierName?: string | null;
    createdAt: string;
    notes?: string | null;
    [key: string]: unknown;
}

interface CustomerDashboardProps {
    customer: {
        id: string;
        barcode: string;
        name: string;
        phone: string;
        pointsBalance: number;
        transactions?: Transaction[];
    };
    viewOnly?: boolean;
    onTransactionSuccess: () => void;
}

export function CustomerDashboard({
    customer,
    viewOnly = false,
    onTransactionSuccess,
}: CustomerDashboardProps) {
    const transactions = customer.transactions || [];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Header Card */}
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: { xs: 2, sm: 3 },
                    flexDirection: { xs: 'column', sm: 'row' }
                }}>
                    {/* Left side: Name, Barcode, Phone (no avatar) */}
                    <Box sx={{ flex: 1, width: '100%' }}>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>{customer.name}</Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {!viewOnly && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <QrCode fontSize="small" sx={{ color: "text.secondary" }} />
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: "text.secondary", 
                                            direction: 'ltr', 
                                            textAlign: 'left',
                                            fontFamily: 'monospace',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        {customer.barcode}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Phone fontSize="small" sx={{ color: "text.secondary" }} />
                                <Typography variant="body2" sx={{ color: "text.secondary", direction: 'ltr', textAlign: 'left' }}>
                                    {customer.phone}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right side: Points Balance Box */}
                    <Box sx={{
                        background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                        p: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        textAlign: "center",
                        minWidth: { xs: '100%', sm: 180 },
                        boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)'
                    }}>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, letterSpacing: 0.5 }}>
                            رصيد النقاط
                        </Typography>
                        <Typography sx={{
                            color: "#fff",
                            fontWeight: "bold",
                            mt: 0.5,
                            direction: 'ltr',
                            fontSize: {
                                xs: '1.35rem',  // 380px
                                '@media (min-width: 380px)': { fontSize: '1.75rem' },  // 380px-500px
                                sm: '2.25rem'  // 500px+
                            }
                        }}>
                            {customer.pointsBalance.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>
                {viewOnly && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>وضع العرض فقط</AlertTitle>
                        يرجى مسح الباركود للقيام بعمليات
                    </Alert>
                )}
            </Paper>

            {/* New Transaction Form */}
            <Box>
                {!viewOnly ? (
                    <TransactionForm customer={customer} onSuccess={onTransactionSuccess} />
                ) : (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                        <Lock fontSize="large" sx={{ color: "gray", mb: 1 }} />
                        <Typography variant="body1" sx={{ color: "gray" }}>
                            العمليات غير متاحة في وضع البحث برقم الهاتف
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Recent Transactions */}
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>آخر المعاملات</Typography>
                {transactions.length > 0 ? (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>الكاشير</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>النوع</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>المبلغ</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>التاريخ</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.slice(0, 5).map((tx) => (
                                    <TableRow
                                        key={tx.id}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                            },
                                            transition: 'background-color 0.2s',
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {tx.cashierName || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {tx.type === "EARN" ? (
                                                <Chip 
                                                    label="إضافة" 
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E8F5E9',
                                                        color: (theme) => theme.palette.mode === 'dark' ? '#E8F5E9' : '#1B5E20',
                                                        fontWeight: 700,
                                                        borderRadius: 1.5,
                                                    }} 
                                                />
                                            ) : (
                                                <Chip 
                                                    label="خصم" 
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#B71C1C' : '#FFEBEE',
                                                        color: (theme) => theme.palette.mode === 'dark' ? '#FFEBEE' : '#B71C1C',
                                                        fontWeight: 700,
                                                        borderRadius: 1.5,
                                                    }} 
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: tx.amountEGP ? 'text.primary' : 'primary.main',
                                                }}
                                            >
                                                {tx.amountEGP ? `${tx.amountEGP} ج.م` : tx.pointsDelta ? `${Math.abs(tx.pointsDelta)} نقطة` : "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr', display: 'block' }}>
                                                {new Date(tx.createdAt).toLocaleDateString('ar-EG', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}
                                                <br />
                                                {new Date(tx.createdAt).toLocaleTimeString('ar-EG', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">لا توجد معاملات سابقة</Typography>
                )}
            </Paper>
        </Box>
    );
}
